// Corner ticker — top-right, always mounted when the combat overlay toggle
// is anything other than 'off'. Renders the active-fight count, the
// per-region near-fight summaries, and the rolled-up distant line.
//
// Data comes from `summarizeCombatDrawer` (pure helper in render/) — this
// component is a thin renderer over its output.
//
// Spec: docs/02-systems/combat-visualisation.md §1a, §1b, §4.

import { type MessageKey, useT } from '../../i18n/index.js';
import { plural } from '../../i18n/rich.js';
import { appDom } from '../../lib/appDom.js';
import type { CombatAxialCoord } from '../../render/shouldAnimateCombat.js';
import { type CombatDrawerNearFight, labelRegion } from '../../render/summarizeCombatDrawer.js';

interface CombatDrawerTickerProps {
  readonly activeCount: number;
  readonly nearFights: ReadonlyArray<CombatDrawerNearFight>;
  readonly distantCount: number;
  readonly localCapital: CombatAxialCoord | null;
  readonly onClick?: () => void;
}

const MAX_VISIBLE_FIGHTS = 3;

// `labelRegion` returns an English compass id; map it onto the dictionary.
const REGION_KEYS: Readonly<Record<string, MessageKey>> = {
  capital: 'combat.region.capital',
  north: 'combat.region.north',
  south: 'combat.region.south',
  east: 'combat.region.east',
  west: 'combat.region.west',
};

export function CombatDrawerTicker({
  activeCount,
  nearFights,
  distantCount,
  localCapital,
  onClick,
}: CombatDrawerTickerProps) {
  const t = useT();
  if (activeCount <= 0) return null;
  const distant =
    distantCount > 0 ? t(plural('combat.distant', distantCount), { count: distantCount }) : null;
  const region = (position: CombatAxialCoord) => {
    const id = labelRegion(position, localCapital);
    const key = REGION_KEYS[id];
    return key ? t(key) : id;
  };
  const visible = nearFights.slice(0, MAX_VISIBLE_FIGHTS);
  const overflow = nearFights.length - visible.length;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="combat-ticker"
      {...appDom({ id: 'combat-ticker', type: 'Toast', role: 'button', scope: 'toast' })}
      aria-label={t(plural('combat.tickerLabel', activeCount), { count: activeCount })}
      className="fixed top-(--hud-panel-top) right-3 md:right-(--centre-inset) z-40 flex max-w-xs flex-col gap-1 rounded-xl border border-rose-500/30 bg-panel-scrim px-3 py-2 cursor-pointer text-left text-foreground shadow-xl backdrop-blur-sm transition hover:bg-card"
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
        {t('combat.active', { count: activeCount })}
      </span>
      {visible.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-xs">
          {visible.map((f) => (
            <li key={`${f.attackerId}::${f.defenderId}`}>
              <span className="text-amber-700 dark:text-amber-300">{region(f.position)}</span>
              <span className="text-muted-foreground">: </span>
              <span>
                {t('combat.vs', {
                  attacker: unitLabel(f.attackerId),
                  defender: unitLabel(f.defenderId),
                })}
              </span>
            </li>
          ))}
          {overflow > 0 ? (
            <li className="text-muted-foreground italic">
              {t('combat.moreNearby', { count: overflow })}
            </li>
          ) : null}
        </ul>
      ) : null}
      {distant ? <p className="text-xs italic text-muted-foreground">{distant}</p> : null}
    </button>
  );
}

function unitLabel(id: string): string {
  const stripped = id.replace(/^unit_/, '');
  return `#${stripped}`;
}
